<template>
	<div class="survey">
		<div class="survey-container">
			<div class="d-flex justify-space-between mb-7">
				<div>
					<h2 class="mr-5">Enkät om el och energi</h2>
					<p>– tack för att du bidrar!</p>
				</div>
				<div style="text-align: right; max-width: 200px; max-height: 200px;" v-if="endUser && endUser.client && endUser.client.logo">
          <img
            style="max-width: 200px; max-height: 200px;"
            :src="$apiUrl + '/storage/' + endUser.client.logo"
            :alt="$processText('{clientName} Logo')"
          />
				</div>
			</div>

			<!-- Page 1: NULÄGE + ATTITYDER					-->
			<div v-if="stepIndex === 0">
				<p>
					I denna enkät får du svara på frågor om fossilfri el och energianvändning och hur du gör idag. Dina svar är värdefulla – oavsett om du vet vad du har för elavtal eller ej och både om du använder mycket eller lite energi. Vi använder svaren för att skapa ännu bättre förutsättningar för smart el- och energianvändning för dig och oss alla.
				</p>
				<p>
					Det går snabbt att svara.
				</p>
				<p>
					Undersökningen genomförs av Nudgd på uppdrag av Strängnäs kommun. Läs mer om hur <a :href="$processText('https://data.nudgd.se/{slug}')" target="_blank">dina personuppgifter hanteras.</a>
				</p>
        <!-- Fallback image -->
        <img
          v-if="!serviceImage"
          style="float: right;"
          width="150"
          :src="$apiUrl + '/storage/strangnas-iphone-elplattform-1670325925012wY.png'"
        />

        <!-- Dynamic image -->
        <img
          v-if="serviceImage"
          style="float: right;"
          width="300"
          :src="$apiUrl + '/storage/' + serviceImage"
        />
				<div class="mt-2 mb-4" :class="{surveyError: errors.includes('usedSmartNudges')}">
					Du fick för ett tag sen ett SMS från kommunen med en länk till en webbsida om elavtal och energibesparing. <strong>Gick du in på den webbsidan?</strong>
				</div>

				<div class="survey-radio-row mb-6">
					<v-chip-group class="mt-0" active-class="primary--text" column v-model="usedSmartNudges">
					    <v-chip outlined value="Ja">Ja</v-chip>
					    <v-chip outlined value="Nej">Nej</v-chip>
					</v-chip-group>
				</div>

				<div style="clear:both;"></div>

				<div v-if="usedSmartNudges === 'Ja'">

					<h4 class="mt-10 mb-4">
						Webbsidan fick mitt hushåll att…
					</h4>

					<div class="survey-radio-row block">
						<div class="survey-radio-row-first block" :class="{surveyError: errors.includes('bytaFossilfritt')}">
							…byta till 100% fossilfritt elavtal <small>(Fossilfritt innebär att din el kommer från sol, vind, vattenkraft, biobränsle och/eller kärnkraft)</small></div>
						<v-radio-group class="mt-2 block" v-model="bytaFossilfritt" column>
									<v-radio label="Ja" value="Ja"></v-radio>
									<v-radio label="Ja, hushållet bytte t.o.m. till ett helt förnybart avtal (med el från endast sol, vind, vattenkraft eller biobränsle)" value="Ja, hushållet bytte t.o.m. till ett helt förnybart avtal (med el från endast sol, vind, vattenkraft eller biobränsle)"></v-radio>
									<v-radio label="Fick mig att planera att göra det/sätta mig in i det" value="Fick mig att planera att göra det/sätta mig in i det"></v-radio>
									<v-radio label="Nej" value="Nej"></v-radio>
									<v-radio label="Hade redan sådant elavtal" value="Hade redan sådant elavtal"></v-radio>
							</v-radio-group>
						</div>

						<div class="survey-radio-row block">
							<div class="survey-radio-row-first block" :class="{surveyError: errors.includes('bytaTimpris')}">…byta till timprisavtal</div>
						<v-radio-group class="mt-2" v-model="bytaTimpris" column>
									<v-radio label="Ja" value="Ja"></v-radio>
									<v-radio label="Fick mig att planera att göra det/sätta mig in i det" value="Fick mig att planera att göra det/sätta mig in i det"></v-radio>
									<v-radio label="Nej" value="Nej"></v-radio>
									<v-radio label="Hade redan sådant elavtal" value="Hade redan sådant elavtal"></v-radio>
						</v-radio-group>
						</div>

						<div class="survey-radio-row block">
							<div class="survey-radio-row-first block" :class="{surveyError: errors.includes('bytaBeteende')}">…göra någon åtgärd eller ändra mitt beteende för att spara energi</div>

							<v-radio-group v-model="bytaBeteende" column>
									<v-radio label="Ja" value="Ja"></v-radio>
									<v-radio label="Fick mig att planera att göra det/sätta mig in i det" value="Fick mig att planera att göra det/sätta mig in i det"></v-radio>
									<v-radio label="Nej" value="Nej"></v-radio>
							</v-radio-group>
						</div>

						<template v-if="bytaBeteende !== 'Nej'">
							<h4 class="mb-3 mt-10">Vilken åtgärd eller på vilket sätt har du ändrat beteende?</h4>
							<p class="mb-2"><em class="smaller-em">
								(ej obligatorisk)
							</em></p>
							<v-textarea rows="4" hide-details solo v-model="commentary"></v-textarea>
							<div class="survey-help mt-2">(Vänligen lämna inga personuppgifter i detta fält)</div>
						</template>
				</div>

				<div class="nudgd-btn inline survey-submit mt-10" @click="nextStep()">Gå vidare</div>
				<div style="clear:both;"></div>
			</div>

			<!-- Page 2: ANGÅENDE TJÄNSTEN -->
			<div v-if="stepIndex === 1">
				<template v-if="usedSmartNudges !== 'Ja'">
					<h4 class="mt-2 mb-3" :class="{surveyError: errors.includes('fossilfrittElavtal')}">
						Är hushållets elavtal ett s.k. fossilfritt elavtal eller inte?
					</h4>
					<p class="mb-2"><em class="smaller-em">
						(Fossilfritt elavtal innebär att din el kommer från sol, vind, vattenkraft, biobränsle och/eller kärnkraft)
					</em></p>
					<div class="survey-radio-row mb-6">
						<v-radio-group class="mt-0" v-model="fossilfrittElavtal" column>
							<v-radio label="Ja" value="Ja"></v-radio>
							<v-radio label="Nej, men har påbörjat ett byte till fossilfritt elavtal (inom senaste 4 veckorna)" value="Nej, men har påbörjat ett byte till fossilfritt elavtal (inom senaste 2 veckorna)"></v-radio>
							<v-radio label="Nej, men planerar att byta inom kort" value="Nej, men planerar att byta inom kort"></v-radio>
							<v-radio label="Nej, och planerar inte att byta" value="Nej, och planerar inte att byta"></v-radio>
							<v-radio label="Vet inte" value="Vet inte"></v-radio>
						</v-radio-group>
					</div>
				</template>

				<h4 class="mt-2 mb-3" :class="{surveyError: errors.includes('viktigastVidByteElavtal')}">
					Vad är viktigast för ditt hushåll i valet av elavtal?
				</h4>
				<p class="mb-2"><em class="smaller-em">
					(Fossilfritt, icke-fossilfritt, förnybart osv)
				</em></p>
				<div class="survey-radio-row mb-6">
					<v-radio-group class="mt-0" v-model="viktigastVidByteElavtal" column>
						<v-radio label="Priset" value="Priset"></v-radio>
						<v-radio label="Klimatet" value="Klimatet"></v-radio>
						<v-radio label="Schysst elhandel (certifiering för elbolags säljmetoder och avtalsformer)" value="Schysst elhandel (certifiering för elbolags säljmetoder och avtalsformer)"></v-radio>
						<v-radio label="Vad familjemedlemmar eller vänner har" value="Vad familjemedlemmar eller vänner har"></v-radio>
						<v-radio label="Annat (ange gärna en kommentar)" value="Annat (ange gärna en kommentar)"></v-radio>
					</v-radio-group>
				</div>

				<v-textarea rows="4" hide-details solo placeholder="Kommentar" v-model="viktigastVidByteElavtalCommentary" v-if="viktigastVidByteElavtal == 'Annat (ange gärna en kommentar)'"></v-textarea>

				<template v-if="fossilfrittElavtal !== 'Ja' && bytaFossilfritt !== 'Ja' && bytaFossilfritt !== 'Ja, hushållet bytte t.o.m. till ett helt förnybart avtal (med el från endast sol, vind, vattenkraft eller biobränsle)'">
					<h4 class="mt-2 mb-3" :class="{surveyError: errors.includes('motivationAttByta')}">
						Vad skulle kunna få hushållet att byta till fossilfritt (eller förnybart) elavtal?
					</h4>
					<p class="mb-2"><em class="smaller-em">
						(Välj de alternativ som vore viktigast för dig)
					</em></p>
					<v-checkbox class="mb-5" hide-details v-model="motivationAttByta" value="Mer info om vad det innebär" label="Mer info om vad det innebär"></v-checkbox>
					<v-checkbox class="mb-5" hide-details v-model="motivationAttByta" value="Rekommendation från familj/vän" label="Rekommendation från familj/vän"></v-checkbox>
					<v-checkbox class="mb-5" hide-details v-model="motivationAttByta" value="Billigare eller samma pris som andra elavtal" label="Billigare eller samma pris som andra elavtal"></v-checkbox>
					<v-checkbox class="mb-5" hide-details v-model="motivationAttByta" value="Att många/allt fler har det" label="Att många/allt fler har det"></v-checkbox>
					<v-checkbox class="mb-5" hide-details v-model="motivationAttByta" value="Att miljönyttan ökar" label="Att miljönyttan ökar"></v-checkbox>
					<v-checkbox class="mb-5" hide-details v-model="motivationAttByta" value="Att bytet är väldigt enkelt" label="Att bytet är väldigt enkelt"></v-checkbox>
					<v-checkbox class="mb-5" hide-details v-model="motivationAttByta" value="Annat (ange gärna en kommentar)" label="Annat (ange gärna en kommentar)"></v-checkbox>

					<v-textarea rows="4" hide-details solo placeholder="Kommentar" v-model="motivationAttBytaCommentary" v-if="motivationAttByta.includes('Annat (ange gärna en kommentar)')"></v-textarea>
				</template>

				<h4 class="mt-2 mb-3" :class="{surveyError: errors.includes('elavtalsTyp')}">
					Vilken avtalstyp har hushållet?
				</h4>
				<p class="mb-2"><em class="smaller-em">
					(Med “timpris” nedan avses avtal där du betalar det elpris som elbörsen har i just den stund som du använder elen. )
				</em></p>

				<div class="survey-radio-row mb-6">
					<v-chip-group active-class="primary--text" column v-model="elavtalsTyp">
					    <v-chip outlined value="Fast pris">Fast pris</v-chip>
					    <v-chip outlined value="Rörligt pris (ej timpris)">Rörligt pris (ej timpris)</v-chip>
					    <v-chip outlined value="Timpris">Timpris</v-chip>
					    <v-chip outlined value="Mix">Mix</v-chip>
					    <v-chip outlined value="Vet inte">Vet inte</v-chip>
					</v-chip-group>
				</div>


				<div style="display:flex; justify-content:space-between;">
					<div class="nudgd-btn small survey-submit" @click="prevStep()">Tillbaka</div>	
					<div class="nudgd-btn survey-submit" @click="nextStep()">Gå vidare</div>
				</div>
			</div>

			<!-- Page 3: DEMOGRAFI, FÖRBRUKNING, FÖRUTSÄTTNINGAR					-->
			<div v-if="stepIndex === 2">

				<h4 class="mt-2 mb-3" :class="{surveyError: errors.includes('fastighetsTyp')}">
					Vilket är ditt huvudsakliga boende?
				</h4>

				<v-chip-group class="mt-0" active-class="primary--text" column v-model="fastighetsTyp">
					<v-chip outlined value="Lägenhet">Lägenhet</v-chip>
					<v-chip outlined value="Villa">Villa</v-chip>
					<v-chip outlined value="Radhus">Radhus</v-chip>
					<v-chip outlined value="Fritidshus">Fritidshus</v-chip>
				</v-chip-group>

				<h4 class="mt-2 mb-3" :class="{surveyError: errors.includes('bostadByggdes')}">
					När byggdes bostaden?
				</h4>

				<v-chip-group class="mt-0" active-class="primary--text" column v-model="bostadByggdes">
					<v-chip outlined value="Före 1930">Före 1930</v-chip>
					<v-chip outlined value="1930–1960">1930–1960</v-chip>
					<v-chip outlined value="1961–1975">1961–1975</v-chip>
					<v-chip outlined value="1976–1990">1976–1990</v-chip>
					<v-chip outlined value="1991–2009">1991–2009</v-chip>
					<v-chip outlined value="2010 eller senare">2010 eller senare</v-chip>
					<v-chip outlined value="Vet inte">Vet inte</v-chip>
				</v-chip-group>

				<h4 class="mt-2 mb-3" :class="{surveyError: errors.includes('antalPersoner')}">
					Hur många personer bor i hushållet?
				</h4>

				<v-chip-group class="mt-0" active-class="primary--text" column v-model="antalPersoner">
					<v-chip outlined value="1">1</v-chip>
					<v-chip outlined value="2">2</v-chip>
					<v-chip outlined value="3">3</v-chip>
					<v-chip outlined value="4">4</v-chip>
					<v-chip outlined value="5">5</v-chip>
					<v-chip outlined value="Fler än 5">Fler än 5</v-chip>
				</v-chip-group>

				<template v-if="fastighetsTyp === 'Lägenhet'">
					<h4 class="mt-2 mb-3" :class="{surveyError: errors.includes('uppvarmningsTypLagenhet')}">
						Ingår värme i hushållets hyra/avgift eller hamnar den på elräkningen?

						<p class="mb-2 mt-2"><em class="smaller-em">
							<a style="font-weight:normal; font-style:normal;" @click="openUppvarmningModal">Om kallhyra</a>

						    <v-dialog
						    	v-model="uppvarmningModal"
						    	width="'100%'"
						    	max-width="900"
						    	scrollable
						    	class="dialog"
						    	@click:outside="closeUppvarmning()"
						    	@keydown.esc="closeUppvarmning()"
						    >

								<v-card>
									<v-card-title class="card-dialog-title" style="font-weight:700;">
										<div>
										<h4>Om kallhyra</h4>
										</div>

										<div @click="closeUppvarmning()" class="card-dialog-close">
											<img src="@/assets/img/clear.png" width="20">
										</div>
									</v-card-title>

									<v-card-text>
										<p>När hyresrätter betalar värmen som en del av sin elräkning kallas det för kallhyra. Ofta är detta fallet om du har direktverkande el som huvudsaklig uppvärmningsmetod, d.v.s. elektriska element.</p>
									</v-card-text>
								</v-card>

						    </v-dialog>
						</em></p>
					</h4>

					<v-chip-group class="mt-0" active-class="primary--text" column v-model="uppvarmningsTypLagenhet">
						<v-chip outlined value="Ingår">Ingår</v-chip>
						<v-chip outlined value="Separat/på elräkningen">Separat/på elräkningen</v-chip>
					</v-chip-group>
				</template>

				<template v-if="fastighetsTyp !== 'Lägenhet'">
					<h4 class="mt-2 mb-3" :class="{surveyError: errors.includes('uppvarmningsTyp')}">
					  Hur värms hushållet boende upp?
					</h4>
					<p class="mb-2"><em class="smaller-em">
						(Du kan ange fler än ett alternativ. <a @click="openUppvarmningModal">Info om uppvärmningsmetoderna</a>

					    <v-dialog
					    	v-model="uppvarmningModal"
					    	width="'100%'"
					    	max-width="900"
					    	scrollable
					    	class="dialog"
					    	@click:outside="closeDialog()"
					    	@keydown.esc="closeDialog()"
					    >

							<v-card>
								<v-card-title class="card-dialog-title" style="font-weight:700;">
									<div>
									<h4>Info om uppvärmningsmetoderna</h4>
									</div>

									<div @click="closeUppvarmning()" class="card-dialog-close">
										<img src="@/assets/img/clear.png" width="20">
									</div>
								</v-card-title>

								<v-card-text>
									<strong>Fjärrvärme</strong><br/>
									<em class="smaller-em">
										Hett vatten kommer in i huset via rörledningar från energileverantören. Detta vatten värmer i sin tur upp det vatten som används till kranar, duschar, element och eventuell golvvärme.
									</em>

									<br><br>
									<strong>Direktverkande el</strong><br/>
									<em class="smaller-em">
										Direktverkande el innebär att du har eldrivna element som värmer upp rummen genom värmestrålning och/eller konvektion (d.v.s att luft värms upp av elementet). Direktverkande el används alltså inte till att värma upp vatten. Direktverkande el är särskilt vanligt i hus från 70-talet.
									</em>

									<br><br>
									<strong>Elpanna</strong><br/>
									<em class="smaller-em">
										En elpanna är en panna som drivs av el. Pannan värmer upp det vatten som används till kranar, duschar, element och eventuell golvvärme.
									</em>

									<br><br>
									<strong>Annan panna</strong><br/>
									<em class="smaller-em">
										En panna som eldas med olja, gas, ved eller pellets värmer upp det vatten som används till kranar, duschar, element och eventuell golvvärme. I många pannor finns även elpatroner som kan användas om det ordinarie bränslet tar slut.
									</em>

									<br><br>
									<strong>Värmepump som värmer vatten</strong><br/>
									<em class="smaller-em">
										En värmepump använder värme ur berggrunden, ur marken (kallas även jordvärme), i sjövatten eller från luften för att värma upp det vatten som används till kranar, duschar, element och eventuell golvvärme. (Luft/luftvärmepump, som tar vara på värmen i uteluften och värmer upp inomhusluften, snarare än vatten, ingår alltså inte här.)
									</em>

									<br><br>
									<strong>Luft/luftvärmepump</strong><br/>
									<em class="smaller-em">
										En luft/luftvärmepump tar vara på värmen i uteluften och värmer upp luften inomhus. Den består av en utedel och en innedel. Det används alltså inte till att värma upp vatten. Oftast är det en kompletterande uppvärmningsmetod.
									</em>

									<br><br>
									<strong>Kamin</strong><br/>
									<em class="smaller-em">
										Hett vatten kommer in i huset via rörledningar från energileverantören. Detta vatten värmer i sin tur upp det vatten som används till kranar, duschar, element och eventuell golvvärme.
									</em>

									<br><br>
									<strong>Kamin</strong><br/>
									<em class="smaller-em">
										Eldning av ved, pellets, spån eller flis i braskamin, kakelugn, öppen spis eller vedspis. Oftast värmer kaminen upp luften i rummet men det finns även kaminer som värmer upp vatten.
									</em>

									<br><br>
									<strong>Annan uppvärmningsmetod</strong><br/>
									<em class="smaller-em">
										T.ex. frånluftsvärmepump, som kan finnas i hus med mekanisk ventilation. Med en sådan värmepump används värme ur luften som sugs ut ur husets kök och våtutrymmen till att värma upp det vatten som används till kranar, duschar, element och eventuell golvvärme.
									</em>
								</v-card-text>
							</v-card>

					    </v-dialog>)
					</em></p>
					<v-checkbox class="mb-5" hide-details v-model="uppvarmningsTyp" @change="guardUppvarmning" value="Fjärrvärme" label="Fjärrvärme"></v-checkbox>
					<v-checkbox class="mb-5" hide-details v-model="uppvarmningsTyp" @change="guardUppvarmning" value="Direktverkande el" label="Direktverkande el"></v-checkbox>
					<v-checkbox class="mb-5" hide-details v-model="uppvarmningsTyp" @change="guardUppvarmning" value="Elpanna" label="Elpanna"></v-checkbox>
					<v-checkbox class="mb-5" hide-details v-model="uppvarmningsTyp" @change="guardUppvarmning" value="Annan panna" label="Annan panna"></v-checkbox>
					<v-checkbox class="mb-5" hide-details v-model="uppvarmningsTyp" @change="guardUppvarmning" value="Värmepump som värmer vatten" label="Värmepump som värmer vatten"></v-checkbox>
					<v-checkbox class="mb-5" hide-details v-model="uppvarmningsTyp" @change="guardUppvarmning" value="Luft/luftvärmepump" label="Luft/luftvärmepump"></v-checkbox>
					<v-checkbox class="mb-5" hide-details v-model="uppvarmningsTyp" @change="guardUppvarmning" value="Kamin" label="Kamin"></v-checkbox>
					<v-checkbox class="mb-5" hide-details v-model="uppvarmningsTyp" @change="guardUppvarmning" value="Annan uppvärmningsmetod" label="Annan uppvärmningsmetod"></v-checkbox>
					<v-checkbox class="mb-5" hide-details v-model="uppvarmningsTyp" @change="guardUppvarmning" value="Vet inte" label="Vet inte"></v-checkbox>

					<v-textarea rows="4" hide-details solo placeholder="Ange gärna hur ditt boende värms upp:" v-model="uppvarmningsTypCommentary" v-if="showUppvarmningsTypCommentary"></v-textarea>
				</template>

				<h4 class="mt-2 mb-3" :class="{surveyError: errors.includes('kwhAr')}">
					Hur mycket el (kwh) använder hushållet per år?
				</h4>
				<p class="mb-2"><em class="smaller-em">
					(Genomsnitt är: 2 000 kwh/år för liten lägenhet, 5 000 kwh/år för stor lägenhet, 5 000 kwh/år för villa med fjärrvärme och 20 000 kwh/år för eluppvärmd villa.)
				</em></p>

				<v-chip-group class="mt-0" active-class="primary--text" column v-model="kwhAr">
					<v-chip outlined value="mindre än 2 000">Mindre än 2 000</v-chip>
					<v-chip outlined value="2000 – 4000">2 000 – 4 000</v-chip>
					<v-chip outlined value="4001 – 6000">4 001 – 6 000</v-chip>
					<v-chip outlined value="6001 – 8000">6 001 – 8 000</v-chip>
					<v-chip outlined value="8001 – 10 000">8001 – 10 000</v-chip>
					<v-chip outlined value="10 001 – 12 000">10 001 – 12 000</v-chip>
					<v-chip outlined value="12 001 – 14 000">12 001 – 14 000</v-chip>
					<v-chip outlined value="14 001 – 16 000">14 001 – 16 000</v-chip>
					<v-chip outlined value="16 001 – 18 000">16 001 – 18 000</v-chip>
					<v-chip outlined value="18 001 – 20 000">18 001 – 20 000</v-chip>
					<v-chip outlined value="20 001 – 22 000">20 001 – 22 000</v-chip>
					<v-chip outlined value="22 001 – 24 000">22 001 – 24 000</v-chip>
					<v-chip outlined value="24 001 – 26 000">24 001 – 26 000</v-chip>
					<v-chip outlined value="mer än 26 000">Mer än 26 000</v-chip>
				</v-chip-group>

				<template v-if="fastighetsTyp === 'Lägenhet'">
					<h4 class="mt-2 mb-3" :class="{surveyError: errors.includes('boyta')}">
						Ungefär hur stor är din lägenhet (m<sup>2</sup>)?
					</h4>

					<v-chip-group class="mt-0" active-class="primary--text" column v-model="boyta">
						<v-chip outlined value="0 - 49">0 – 49</v-chip>
						<v-chip outlined value="50 - 79">50 – 79</v-chip>
						<v-chip outlined value="80 – 119">80 – 119</v-chip>
						<v-chip outlined value="120 – 149">120 – 149</v-chip>
						<v-chip outlined value="150 - 199">150 – 199</v-chip>
						<v-chip outlined value="mer än 200">Mer än 200</v-chip>
						<v-chip outlined value="Vet inte">Vet inte</v-chip>
					</v-chip-group>
				</template>

				<template v-if="fastighetsTyp !== 'Lägenhet'">
					<h4 class="mt-2 mb-3" :class="{surveyError: errors.includes('boyta')}">
						Ungefär hur stor är hushållets UPPVÄRMDA yta (m<sup>2</sup>)?
					</h4>
					<p class="mb-2">
						Huvudbyggnad:<br/>
						<em class="smaller-em">
							(Den huvudsakliga byggnaden. Räkna alltså inte här med komplementbyggnader som är uppvärmda merparten av året. Dessa anger du nedan.)
						</em>
					</p>
					<v-chip-group class="mt-0" active-class="primary--text" column v-model="boyta">
						<v-chip outlined value="0 - 49">0 – 49</v-chip>
						<v-chip outlined value="50 - 79">50 – 79</v-chip>
						<v-chip outlined value="80 – 119">80 – 119</v-chip>
						<v-chip outlined value="120 – 149">120 – 149</v-chip>
						<v-chip outlined value="150 - 199">150 – 199</v-chip>
						<v-chip outlined value="mer än 200">Mer än 200</v-chip>
						<v-chip outlined value="Vet inte">Vet inte</v-chip>
					</v-chip-group>

					<p class="mb-2 mt-4" :class="{surveyError: errors.includes('biyta')}">
						Komplementbyggnad:<br/>
						<em class="smaller-em">
							(T.ex. uterum, friggebod, attefallshus, garage, växthus. Räkna bara in dem som är uppvärmda merparten av året. Ange den totala yta.)
						</em>
					</p>
					<v-chip-group class="mt-0" active-class="primary--text" column v-model="biyta">
						<v-chip outlined value="Har ingen komplementbyggnad">Har ingen komplementbyggnad</v-chip>
						<v-chip outlined value="0 - 49">0 – 49</v-chip>
						<v-chip outlined value="50 - 79">50 – 79</v-chip>
						<v-chip outlined value="80 – 119">80 – 119</v-chip>
						<v-chip outlined value="120 – 149">120 – 149</v-chip>
						<v-chip outlined value="150 - 199">150 – 199</v-chip>
						<v-chip outlined value="mer än 200">Mer än 200</v-chip>
						<v-chip outlined value="Vet inte">Vet inte</v-chip>
					</v-chip-group>

					<h4 class="mt-2 mb-3" :class="{surveyError: errors.includes('solceller')}">
						Har hushållet solceller?
					</h4>

					<v-chip-group class="mt-0" active-class="primary--text" column v-model="solceller">
						<v-chip outlined value="Ja">Ja</v-chip>
						<v-chip outlined value="Nej men planerar att skaffa">Nej men planerar att skaffa</v-chip>
						<v-chip outlined value="Nej">Nej</v-chip>
					</v-chip-group>

					<template v-if="solceller === 'Ja'">
						<h4 class="mt-2 mb-3" :class="{surveyError: errors.includes('solcellerEffekt')}">
							Hur stor installerad effekt solel har hushållet?
						</h4>

						<v-select v-model="solcellerEffekt" label="Välj en effekt" :items="[
							'0–1,9 kW',
							'2–3,9 kW',
							'4–5,9 kW',
							'6–7,9 kW',
							'8–9,9 kW',
							'10–11,9 kW',
							'12–13,9 kW',
							'14–15,9 kW',
							'16–17,9 kW',
							'18–20 kW',
							'Över 20 kW'
						]" solo></v-select>
					</template>
				</template>

				<h4 class="mt-2 mb-3" :class="{surveyError: errors.includes('intresseAndelSolcellspark')}">
				  Är ditt hushåll intresserat av att köpa andelar i en lokal solcellspark? <a style="font-weight:normal; font-style:normal;" @click="openSolcellspark">[?]</a>
				</h4>

				<v-chip-group class="mt-0" active-class="primary--text" column v-model="intresseAndelSolcellspark">
					<v-chip outlined value="Ja">Ja</v-chip>
					<v-chip outlined value="Nej">Nej</v-chip>
					<v-chip outlined value="Kanske">Kanske</v-chip>
					<v-chip outlined value="Vet inte">Vet inte</v-chip>
				</v-chip-group>

				<v-dialog
					v-model="solcellsparkModal"
					width="'100%'"
					max-width="900"
					scrollable
					class="dialog"
					@click:outside="closeSolcellspark()"
					@keydown.esc="closeSolcellspark()"
				>

					<v-card>
						<v-card-title class="card-dialog-title" style="font-weight:700;">
							<div>
							<h4></h4>
							</div>

							<div @click="closeSolcellspark()" class="card-dialog-close">
								<img src="@/assets/img/clear.png" width="20">
							</div>
						</v-card-title>

						<v-card-text>
							<p>Att bli andelsägare kan vara ett alternativ för dig som vill producera solel men inte kan eller vill sätta solceller på ett eget tak. Att investera i andelar i en solcellspark är dessutom ofta billigare per installerad kilowattimme. Däremot tillkommer rörliga kostnader som för elöverföring. Elen som andelarna producerar köps normalt till självkostnadspris, vilket är billigare och stabilare än priserna på elmarknaden. Samtidigt bidrar du till att mer förnybar el produceras. En riktig klimatinsats!</p>

							<p>Ditt svar kan inte kopplas till dig och frågan är endast avsedd att ge en indikation på intresset hos kommunens hushåll, som en del av framtida beslutsunderlag.</p>
						</v-card-text>
					</v-card>

				</v-dialog>

				<template v-if="intresseAndelSolcellspark">
					<p class="mb-2 mt-2"><em class="smaller-em">
						Kopmmentar (ej obligatorisk)
					</em></p>
					<v-textarea rows="4" hide-details solo v-model="solcellsparkCommentary"></v-textarea>
				</template>

				<h4 class="mt-2 mb-3" :class="{surveyError: errors.includes('elbilar')}">
					Hur många elbilar laddar du i anslutning till hemmet?
				</h4>

				<v-chip-group class="mt-0" active-class="primary--text" column v-model="elbilar">
					<v-chip outlined value="Ingen">Ingen</v-chip>
					<v-chip outlined value="1">1</v-chip>
					<v-chip outlined value="2">2</v-chip>
					<v-chip outlined value="Fler än 2">Fler än 2</v-chip>
				</v-chip-group>

				<h4 class="mt-2 mb-3">
					Vad är ditt bästa tips till andra i Strängnäs kommun som funderar på att byta elavtal eller vill minska sin energianvändning?
				</h4>
				<p class="mb-2"><em class="smaller-em">
					(ej obligatorisk)
				</em></p>
				<v-textarea rows="4" hide-details solo v-model="endCommentary"></v-textarea>
				<div class="survey-help mt-2">(Vänligen lämna inga personuppgifter i detta fält)</div>

				<div class="nudgd-btn survey-submit mt-8" v-if=" ! thankYou" @click="submitResponse()">Skicka in</div>
				<br>
			
				<div class="nudgd-btn small survey-submit mt-0" v-if=" ! thankYou" @click="prevStep()">Tillbaka</div>
				<div style="clear:both;"></div>
			</div>

			<v-progress-linear color="#00A19A" height="10" rounded class="mt-8" :value="(stepIndex + 1) / steps * 100"></v-progress-linear>
		</div>

		    <v-dialog
		    	v-model="thankYou"
		    	width="'100%'"
		    	max-width="900"
		    	scrollable
		    	class="dialog"
		    	persistent
		    >

				<v-card>
					<v-card-title class="card-dialog-title" style="font-weight:700;">
						<div>Tack för att du svarat!</div>
					</v-card-title>

					<v-card-text>
						<p>Du är en viktig del i att ställa om energiförsörjningen och värna elnätet.</p>
						<p>Gå gärna till webbsidan igen för att ta del av tipsen.</p>

						<div class="nudgd-btn inline survey-submit mt-5" @click="submitResponse(true)">Mer om hur du kan bli energismart</div>
					</v-card-text>
				</v-card>

		    </v-dialog>

</div>
</template>

<script>
import axios from 'axios'

export default {
	mounted() {
		this.uuid = Math.random().toString(36).substr(2, 5)
		this.$store.state.surveyId = 10

		// For testing purposes
		if (this.$route.query.page) {
			this.stepIndex = parseInt(this.$route.query.page)
		}

		// Get end user
		axios.get(this.$apiUrl + '/api/end-user/' + this.slug + '?s=1').then(res => {
			this.$store.state.endUser = res.data.endUser
			this.$store.state.client = res.data.endUser.client
			this.$store.state.translations = res.data.translations
		})
	},

	data() {
		return {
			steps: 3,

			fossilfrittElavtal: null,
			viktigastVidByteElavtal: null,
			viktigastVidByteElavtalCommentary: null,
			motivationAttByta: [],
			motivationAttBytaCommentary: null,
			elavtalsTyp: null,
			solcellsparkCommentary: null,
			showUppvarmningsTypCommentary: false,

			usedSmartNudges: null,
			bytaFossilfritt: null,
			bytaTimpris: null,
			bytaBeteende: null,
			commentary: null,

			fastighetsTyp: null,
			bostadByggdes: null,
			antalPersoner: null,
			antalBarn: null,
			kwhAr: null,
			boyta: null,
			biyta: null,
			solceller: null,
			solcellerEffekt: null,
			elbilar: null,
			uppvarmningsTyp: [],
			uppvarmningsTypLagenhet: null,
			uppvarmningsTypCommentary: null,
			intresseAndelSolcellspark: null,
			endCommentary: null,

			uppvarmningModal: false,
			solcellsparkModal: false,

			errors: [],
			engaged: [],
			stepIndex: 0,
			thankYou: false,

			// Each array represents the required fields of a step
			required: [
				[
					'usedSmartNudges'
				],
				[
					'viktigastVidByteElavtal',
					'elavtalsTyp'
				],
				[
					'fastighetsTyp',
					'bostadByggdes',
					'antalPersoner',
					'kwhAr',
					'boyta',
					'elbilar',
					'intresseAndelSolcellspark'
				]
			],
		}
	},

	watch: {
		fossilfrittElavtal(val) { this.engaged.push('fossilfrittElavtal') },
		viktigastVidByteElavtal(val) { this.engaged.push('viktigastVidByteElavtal') },
		motivationAttByta(val) { this.engaged.push('motivationAttByta') },
		elavtalsTyp(val) { this.engaged.push('elavtalsTyp') },
		usedSmartNudges(val) { this.engaged.push('usedSmartNudges') },
		bytaFossilfritt(val) { this.engaged.push('bytaFossilfritt') },
		bytaTimpris(val) { this.engaged.push('bytaTimpris') },
		bytaBeteende(val) { this.engaged.push('bytaBeteende') },
		fastighetsTyp(val) { this.engaged.push('fastighetsTyp') },
		antalPersoner(val) { this.engaged.push('antalPersoner') },
		kwhAr(val) { this.engaged.push('kwhAr') },
		boyta(val) { this.engaged.push('boyta') },
		biyta(val) { this.engaged.push('biyta') },
		solceller(val) { this.engaged.push('solceller') },
		solcellerEffekt(val) { this.engaged.push('solcellerEffekt') },
		elbilar(val) { this.engaged.push('elbilar') },
		antalBarn(val) { this.engaged.push('antalBarn') },
		bostadByggdes(val) { this.engaged.push('bostadByggdes') },
		uppvarmningsTyp(val) { this.engaged.push('uppvarmningsTyp') },
		uppvarmningsTypLagenhet(val) { this.engaged.push('uppvarmningsTypLagenhet') },
		intresseAndelSolcellspark(val) { this.engaged.push('intresseAndelSolcellspark') },
	},

	methods: {
		guardUppvarmning(val) {
			if (val.includes('Annan uppvärmningsmetod')) {
				this.showUppvarmningsTypCommentary = true
			} else {
				this.showUppvarmningsTypCommentary = false
			}

			if (this.uppvarmningsTyp.includes('Vet inte') || val === 'Vet inte') {
				this.uppvarmningsTyp = ['Vet inte']
			}
		},

		selectFrequency(property, value) {
			this[property] = value
		},

		openUppvarmningModal() {
			this.uppvarmningModal = true
		},

		closeUppvarmning() {
			this.uppvarmningModal = false
		},

		openSolcellspark() {
			this.solcellsparkModal = true
		},

		closeSolcellspark() {
			this.solcellsparkModal = false
		},

		nextStep() {
			if ( ! this.validate()) {
				alert('Var god och fyll i alla rödmarkerade fält')
				return
			}

			window.scrollTo(0, 0)
			this.stepIndex++
			this.saveState()
		},

		validate() {
			this.errors = []

			let required = [...this.required[this.stepIndex]]

			if (this.stepIndex === 0) {
				if (this.usedSmartNudges === 'Ja') {
					required = required.concat(['bytaFossilfritt', 'bytaTimpris', 'bytaBeteende'])
				}
			}

			if (this.stepIndex === 1) {
				if (this.usedSmartNudges !== 'Ja') {
					required = required.concat(['fossilfrittElavtal'])
				}
			}

			if (this.stepIndex === 2) {
				if (this.fastighetsTyp !== 'Lägenhet') {
					required = required.concat(['biyta', 'solceller', 'uppvarmningsTyp'])
				} else {
					required = required.concat(['uppvarmningsTypLagenhet'])
				}

				if (this.solceller === 'Ja') {
					required = required.concat(['solcellerEffekt'])
				}
			}

			console.log(required)

			this.errors = required.filter(key => {
				return ! this.engaged.includes(key) || (this.$data[key] instanceof Array && this.$data[key].length === 0)
			})


			this.errors =  this.errors.concat(required.filter(val => ! this.engaged.includes(val)))

			return this.errors.length === 0
		},

		prevStep() {
			window.scrollTo(0, 0)
			this.stepIndex--
		},

		saveState() {
			axios.post(this.$apiUrl + '/api/survey/12', {
				...this.$data,
				uuid: this.uuid,
				slug: this.slug,
				seconds_passed: performance ? parseInt(performance.now() / 1000) : null,
				completed_pages: this.stepIndex + 1
			})
		},

		submitResponse(goToApp) {
			if ( ! this.validate()) {
				alert('Var god och fyll i alla rödmarkerade fält')
				return
			}

			axios.post(this.$apiUrl + '/api/survey/12', {
				...this.$data,
				uuid: this.uuid,
				slug: this.slug,
				seconds_passed: performance ? parseInt(performance.now() / 1000) : null,
				completed_pages: this.stepIndex + 1,
				complete: true
			}).then(res => {
				this.thankYou = true
				this.$store.state.surveySent = true

				if (goToApp) {
					let url = null

					if (this.slug) {
						url = 'https://strangnas.smartelanvandning.se/' + this.slug
					} else {
						url = 'https://strangnas.smartelanvandning.se/'
					}

					window.location.href = url
				}
            })
		}
	},

	computed: {
		slug() {
			// Cookie slug precedence
			return this.$route.params.slug || this.$getCookie('openEndUser')
		},

		endUser() {
			return this.$store.state.endUser
		},

		serviceImage() {
			return this.$store.state.client.customization?.service_img
		},
	}
}
</script>
